import { JsonRpcProvider, JsonRpcSigner } from "@ethersproject/providers";
import { useConnector, useEthers } from "@usedapp/core";
import { BigNumber, ethers } from "ethers";
import { useEffect, useMemo, useState, useCallback } from "react";
import { ActiveNetwork } from "utils/connectors";

type TConnectionName =
  | "Metamask"
  | "CoinbaseWallet"
  | "WalletConnect"
  | undefined;

export function useWallet(expectedChainId: number = ActiveNetwork.chainId) {
  const { account, isLoading, library, activateBrowserWallet, switchNetwork } =
    useEthers();
  const [balance, setBalance] = useState<BigNumber>(BigNumber.from(0));
  const [signer, setSigner] = useState<JsonRpcSigner>();

  const [chainId, setChainId] = useState<number>(ActiveNetwork.chainId);
  const [isLoadingChainId, setIsLoadingChainId] = useState<boolean>(false);

  const { connector: activeConnector } = useConnector();

  const connectorName = useMemo((): TConnectionName => {
    if (!account) {
      return undefined;
    }
    const connectionNames = ["Metamask", "CoinbaseWallet", "WalletConnect"];
    if (connectionNames.includes(activeConnector?.connector?.name || "")) {
      return activeConnector?.connector?.name as TConnectionName;
    } else if (activeConnector?.connector?.name === "Injected") {
      return "WalletConnect";
    }
    return undefined;
  }, [account, activeConnector?.connector]);

  // Fetch signer and initial chainId from library
  useEffect(() => {
    if (!account) return;

    (async () => {
      const provider = library as JsonRpcProvider;
      const signer = provider.getSigner();
      setSigner(signer);

      const network = await provider.getNetwork();
      setChainId(network.chainId);
    })();
  }, [account, library]);

  // Fetch balance separately - re-fetch when chainId changes
  useEffect(() => {
    if (!account) return;

    (async () => {
      try {
        const ethereum = (window as any).ethereum;
        if (ethereum) {
          const provider = new ethers.providers.Web3Provider(ethereum);
          const bal = await provider.getBalance(account);
          setBalance(bal);
        } else {
          const provider = library as JsonRpcProvider;
          const bal = await provider.getBalance(account);
          setBalance(bal);
        }
      } catch (error) {
        console.error("Failed to fetch balance:", error);
        setBalance(BigNumber.from(0));
      }
    })();
  }, [account, chainId, library]);

  const connectToWallet = useCallback(async () => {
    setIsLoadingChainId(true);

    try {
      await activateBrowserWallet();
      await switchNetwork(expectedChainId);

      const provider = library as ethers.providers.JsonRpcProvider;
      const network = await provider.getNetwork();
      setChainId(network.chainId);
    } catch (error) {
      console.error(error);
    }

    setIsLoadingChainId(false);
  }, [activateBrowserWallet, library, switchNetwork, expectedChainId]);

  useEffect(() => {
    const ethereum = (window as any).ethereum;
    if (!ethereum) return;

    const handleChainChanged = (chainIdHex: string | number) => {
      // MetaMask returns hex string (e.g. "0x2105"), parse to number
      const parsed =
        typeof chainIdHex === "string" ? parseInt(chainIdHex, 16) : chainIdHex;
      setChainId(parsed);
    };

    ethereum.on("chainChanged", handleChainChanged);

    // Get current chainId on mount
    if (ethereum.chainId) {
      handleChainChanged(ethereum.chainId);
    }

    return () => ethereum.removeListener("chainChanged", handleChainChanged);
  }, []);

  return {
    account,
    balance,
    signer,
    isLoading: isLoading || isLoadingChainId,
    connectorName,
    provider: library as JsonRpcProvider,
    chainId,
    isConnected: account && chainId === expectedChainId,
    connectToWallet,
  };
}

